import { type FieldPath } from 'react-hook-form';

import { type EditAuditFormBackgroundData } from '#apps/client-web/pages/audit-form-background/types';

import { type Meter } from '#packages/data-collection/models/audit-background-form/categories/general';

export const MeterTypeLabel: Record<Meter['type'], string> = {
  electricity: 'Electricity',
  natural_gas: 'Natural Gas',
  water: 'Water',
  other: 'Other',
} as const;

export const CanadaProvinces = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Nova Scotia',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Northwest Territories',
  'Nunavut',
  'Yukon',
];

export const WEEK_DAY_SHORT_NAMES = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
];

// Field descriptions sourced from EA sheet
// Only fields with descriptions in the sheet are included.
export const BACKGROUND_FIELD_DESCRIPTIONS: Partial<
  Record<FieldPath<EditAuditFormBackgroundData>, string>
> = {
  // General
  'general.address.addressLine1':
    'Do not include the city, province, or postal code in this line.',
  'general.yearBuilt': 'What year was the building constructed?',
  'general.renovations':
    'Describe any major renovations or additions and when they took place.',
  'general.grossFloorArea':
    'What is the gross floor area of the building, i.e. the area of all the floors combined?',
  'general.floors': 'How many floors are there in the building?',
  'general.parkadeLevels':
    'Is there an underground parkade in the building? If so, how many levels is it?',
  'general.otherFloors':
    'Are there any underground levels like basements or partial levels like mezzanines, lofts, penthouses?',
  'general.weatherAtStart': 'Include temperature and overcast observations.',
  'general.weatherAtEnd': 'Include temperature and overcast observations.',
  'general.operationHoursDescription':
    'What is the typical occupied period of the building? Does it differ on weekends? Does it differ seasonally?',
  'general.spaceUsesDescription':
    "Briefly describe the building's space uses or functional areas in the building (e.g. lobby, offices, cafeteria, labs, retail shop, etc.)",
  'general.occupantsDescription':
    "Briefly describe the building's occupants. (i.e. full-time vs part-time, cleaning staff, etc.)",
  'general.occupants.fullTimeEmployees':
    'How many of each type of occupant are in the building on a daily basis? A rough estimate is ok.',
  'general.elevators': 'Are there elevators in the building? If so, how many?',
  'general.exteriorCarOutlets':
    'Are there car outlets / block heaters in the parkade or parking lot? If so, how many? Note that for locations in mild climates, site contacts might not know what this is.',
  'general.recentUpgrades':
    'Have there been any recent upgrades in the building to do with energy efficiency in the last four years? For example to mechanical equipment, lighting, water fixtures, or the building envelope?',
  'general.plansForUpgrades':
    'Are there plans to upgrade any of the items I mentioned in next couple years?',
  'general.auditorTeam.0.tasks':
    'What kind of tasks did each person complete while on site? How was the work divided?',

  // Controls
  'controls.buildingAutomationSystem':
    'Does the building have an automation system? If so, what can the building automation system control, e.g. lights, only boilers, zone setpoints, etc.',

  // Space Heating
  'spaceHeating.systems.0':
    'What are the space heating system(s) in the building? Describe each system. What is the source (e.g. boiler), what is the distribution (e.g. piping), and what are the terminal units (e.g. baseboard radiators, fan coils, etc.).',
  'spaceHeating.hasRecoverySystem':
    'Are there any heat recovery systems in the building?',

  // Space Cooling
  'spaceCooling.systems.0':
    'Is there space cooling in the building? If so, what are the cooling system(s)? Describe source, distribution, and terminal assets.',

  // Ventilation
  'ventilation.system':
    'How is the building ventilated? Describe the different pieces of equipment included in the ventilation system(s).',
  'ventilation.isVariableAirVolumeSystem':
    'Is the ventilation system a variable air volume system? I.e. are there dampers near terminal diffusers which can change positions based on temperature or ventilation needs?',
  'ventilation.hasCO2Sensors':
    'If the ventilation system is a variable air volume system, are there carbon dioxide sensors which control terminal air dampers?',
  'ventilation.hasParkadeCOSensors':
    'If there is a parkade, and the parkade has exhaust fans, are there carbon monoxide sensors in the parkade which control the exhaust fans?',

  // Domestic Cold Water
  'domesticColdWater.otherWaterLoads':
    'Besides water use at water fixtures, for example at taps and toilets, are there any other water uses in the building? For example is there an irrigated area outside?',
  'domesticColdWater.hadPipeLeaks':
    'Have there been any major leaks in piping that would have caused a spike in water consumption in the last four years?',

  // Domestic Hot Water
  'domesticHotWater.system':
    'How is domestic water heated? Describe every system in the building.',

  // Lighting
  'lighting.exteriorLightingControls':
    'What are the exterior lighting controls? E.g. photocells or timers.',

  // Other
  'other.unvisitedRooms':
    "Were there any rooms you weren't able to access during the site audit?",
  'other.extraDescription':
    'Is there anything else I should know about the building before I start an inventory?',
};
